import { createFileRoute } from "@tanstack/solid-router";
import { seo } from "~/utils/seo";

export const Route = createFileRoute("/_main/privacy")({
  head: () => ({
    meta: seo({
      title: "Privacy Policy | ActShark",
      description:
        "Privacy policy for ActShark accounting software. Learn how we collect, use, and protect your financial and personal data.",
      path: "/privacy",
    }),
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <main class="flex min-h-screen flex-col">
      <div class="mx-auto w-full max-w-4xl px-6 py-12 md:py-16 lg:py-20">
        <h1 class="mb-2 text-4xl font-bold">Privacy Policy</h1>
        <p class="mb-8 text-sm text-muted-foreground">
          Last updated: Sep 4, 2026
        </p>

        <p class="mb-6 leading-relaxed text-muted-foreground">
          This Privacy Policy explains how ActShark ("ActShark", "we", "us", or
          "our") collects, uses, discloses, and protects information when you
          use our accounting software and related services, including our
          website at actshark.com (collectively, the "Service").{" "}
          <span class="font-semibold text-foreground">
            Note: ActShark is currently operated by its owner as an individual;
            contracting entity details will be published here upon
            incorporation.
          </span>
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          By using the Service, you agree to the collection and use of
          information in accordance with this policy. If you do not agree,
          please do not use the Service.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">1. Who We Are</h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          ActShark is an accounting software service that lets you manage
          business bookkeeping: documents, journal entries, accounts,
          worksheets, and reports. Contact us at{" "}
          <a
            href="mailto:hello@actshark.com"
            class="text-blue-600 hover:underline"
          >
            hello@actshark.com
          </a>{" "}
          with any privacy questions or requests.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">2. Information We Collect</h2>

        <h3 class="mb-3 mt-6 text-xl font-semibold">Account Information</h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          When you register, we collect your name, email address, and an
          optional profile image. If you link the ActShark Telegram bot, we
          store the linkage between your Telegram account and your ActShark
          account.
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">
          Financial and Accounting Data
        </h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          The core of the Service is processing your financial data. This
          includes the books, chart of accounts, transactions, journal entries,
          worksheets, and fixed-asset records you create, as well as documents
          (invoices, receipts, statements) that you upload. These documents are
          stored in encrypted object storage and processed with local OCR
          (running inside our own infrastructure) to extract text for
          categorization and data entry.
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">Bank Connection Data</h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          If you connect a bank account, we use Plaid as our account aggregation
          provider. We store the access tokens Plaid issues (in encrypted form)
          and the account and transaction data you authorize Plaid to share with
          us. Plaid processes your bank credentials and data under its own{" "}
          <a
            href="https://plaid.com/legal/"
            rel="external nofollow noopener noreferrer"
            target="_blank"
            class="text-blue-600 hover:underline"
          >
            privacy policy and end-user terms
          </a>
          . We never see or store your online banking password.
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">Payment Information</h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          Paid subscriptions are processed by{" "}
          <a
            href="https://stripe.com/privacy"
            rel="external nofollow noopener noreferrer"
            target="_blank"
            class="text-blue-600 hover:underline"
          >
            Stripe
          </a>
          . Your payment card details are collected and stored by Stripe, not by
          us. We receive and store your billing status, subscription tier, and
          your Stripe customer identifier.
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">Usage and Device Data</h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          When you use the Service, we automatically collect log and usage data
          such as IP address, browser type and version, pages visited, time and
          date of visits, and session records (including IP address and user
          agent, retained for security and to keep you signed in).
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">
          Error and Performance Data
        </h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We use Sentry to capture error reports, performance traces, and
          limited browser session replays to diagnose problems. Session replay
          may capture how you interact with the interface; we use it only for
          debugging and support.
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">
          Content You Send to AI Features
        </h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          If you use the AI assistant or AI document extraction, the text of
          your request, relevant document text, and related context from your
          books are sent to our AI providers to generate a response. See section
          6 for details on AI processing.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">3. How We Use Information</h2>
        <ul class="mb-6 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>Provide, operate, maintain, and improve the Service</li>
          <li>Create and manage your account and authenticate you</li>
          <li>
            Process your documents and transactions, including OCR and
            AI-assisted categorization and extraction
          </li>
          <li>Sync and display bank-connected account and transaction data</li>
          <li>
            Bill subscriptions and metered usage through Stripe, and detect
            fraud
          </li>
          <li>Send service, security, and account-related communications</li>
          <li>Monitor for errors, abuse, and security incidents</li>
          <li>Comply with legal obligations and enforce our terms</li>
        </ul>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We do not sell your personal information, and we do not share it with
          third parties for their own marketing purposes.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          4. Legal Bases for Processing (EEA/UK)
        </h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          Where the GDPR applies, we process your data on the following bases:
          performance of a contract (providing the Service and billing);
          legitimate interests (security, fraud prevention, service improvement,
          error monitoring); consent (where required, withdrawable at any time);
          and compliance with legal obligations.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          5. How We Share Information
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          We share information only with the following categories of processors
          and in the following situations:
        </p>
        <ul class="mb-6 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>
            <strong>Stripe</strong> — payment processing and subscription
            management
          </li>
          <li>
            <strong>Plaid</strong> — bank account aggregation (only if you
            connect a bank account)
          </li>
          <li>
            <strong>AI providers</strong> — processing of AI requests (see
            section 6)
          </li>
          <li>
            <strong>IDrive e2</strong> — encrypted object storage for your
            documents and backups
          </li>
          <li>
            <strong>ZeptoMail</strong> — delivery of transactional email (such
            as verification codes)
          </li>
          <li>
            <strong>Sentry</strong> — error monitoring and diagnostics
          </li>
          <li>
            <strong>Inngest</strong> — background job orchestration for document
            processing and other pipelines
          </li>
          <li>
            <strong>Railway</strong> — application hosting and infrastructure
          </li>
          <li>
            <strong>
              Business transfers, legal requirements, and protection of rights
            </strong>{" "}
            — in connection with a merger or asset sale (with notice), when
            required by law or valid legal process, or to protect the rights,
            property, or safety of ActShark, our users, or the public
          </li>
        </ul>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          Your financial data is treated as confidential and is not shared with
          other users. Businesses within your account are isolated from other
          accounts.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          6. AI Processing Disclosure
        </h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          ActShark's AI features (the assistant chat, document data extraction,
          and chat titling) send prompts, document text, and related context to
          third-party large language model providers, reached through OpenRouter
          and similar AI gateways. This may include excerpts of your financial
          documents and bookkeeping data.
        </p>
        <ul class="mb-6 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>
            AI features are optional. Core bookkeeping does not require sending
            data to AI providers.
          </li>
          <li>
            We only send the text needed for the task (for example, extracted
            document text, capped at a size limit) rather than your entire
            books.
          </li>
          <li>
            AI providers process this content on our instructions to generate
            responses; where their terms permit it, we select options that
            exclude use of your content for model training. You should assume
            any content you submit to AI features is processed by those
            providers.
          </li>
          <li>
            AI output can be wrong. You are responsible for reviewing any
            categorization or extraction before relying on it. See our Terms,
            section on professional advice.
          </li>
        </ul>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          7. Cookies and Local Storage
        </h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We use a small number of strictly necessary cookies and local storage
          to keep you signed in, remember your theme preference, and protect
          against fraud. We do not use advertising or third-party marketing
          cookies. You can control cookies through your browser settings, but
          blocking strictly necessary cookies may prevent the Service from
          working.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">8. Data Retention</h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We retain account, financial, and billing data for as long as your
          account is active and as needed to provide the Service, comply with
          legal and tax obligations, resolve disputes, and enforce our
          agreements. Session and log data are retained for shorter periods
          unless needed for security investigations. When you delete data or
          your account, we remove it from active systems within 30 days, except
          where retention is legally required (see our{" "}
          <a href="/data-deletion" class="text-blue-600 hover:underline">
            Data Deletion Request
          </a>{" "}
          page). Encrypted backups roll off on a fixed schedule.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">9. Data Security</h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We protect your data with encryption in transit (TLS), encryption at
          rest for sensitive tokens and backups, access controls, and audit
          logging. No method of transmission or storage is 100% secure, and we
          cannot guarantee absolute security. If a breach affecting your data
          occurs, we will notify you as required by applicable law.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          10. International Data Transfers
        </h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We are based in the United States and our providers operate in various
          countries. Where personal data is transferred outside the EEA, UK, or
          Switzerland, we rely on appropriate safeguards such as the EU Standard
          Contractual Clauses (or the UK Addendum) with the relevant provider,
          or an adequacy decision.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">11. Your Privacy Rights</h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          Depending on your location, you may have some or all of the following
          rights:
        </p>
        <ul class="mb-4 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>
            <strong>Access / know</strong> what personal data we hold about you
            and the categories collected
          </li>
          <li>
            <strong>Correction</strong> of inaccurate personal data
          </li>
          <li>
            <strong>Deletion</strong> of your personal data (see our{" "}
            <a href="/data-deletion" class="text-blue-600 hover:underline">
              Data Deletion Request
            </a>{" "}
            page)
          </li>
          <li>
            <strong>Portability</strong> — export your books and data from
            within the Service
          </li>
          <li>
            <strong>Objection / restriction</strong> of certain processing
          </li>
          <li>
            <strong>Withdraw consent</strong> where processing is based on
            consent
          </li>
        </ul>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          To exercise any right, email{" "}
          <a
            href="mailto:hello@actshark.com"
            class="text-blue-600 hover:underline"
          >
            hello@actshark.com
          </a>
          . We will verify your identity before fulfilling requests and respond
          within the timeframe required by applicable law (no later than 45 days
          for CCPA requests; one month for GDPR requests).
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">
          California (CCPA/CPRA) Rights
        </h3>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          California residents have the right to know the categories of personal
          information collected, the right to delete, the right to correct, and
          the right to portability. We do not sell or share personal information
          as defined by the CCPA/CPRA, so no opt-out of sale/sharing is
          required. We will not discriminate against you for exercising your
          rights. Authorized agents may submit requests with proof of
          authorization.
        </p>

        <h3 class="mb-3 mt-6 text-xl font-semibold">EEA/UK (GDPR) Rights</h3>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          EEA and UK residents have the rights listed above (access,
          rectification, erasure, restriction, portability, and objection), as
          well as the right to lodge a complaint with a supervisory authority.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">12. Children's Privacy</h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          The Service is not directed to anyone under 18, and we do not
          knowingly collect personal information from children under 13. If you
          believe a child has provided us personal data, contact us and we will
          remove it.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          13. Changes to This Policy
        </h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We may update this Privacy Policy from time to time. Material changes
          will be announced by email and/or a notice within the Service before
          they take effect, and the "Last updated" date at the top will change.
          Your continued use of the Service after changes take effect
          constitutes acceptance of the updated policy.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">14. Contact Us</h2>
        <p class="leading-relaxed text-muted-foreground">
          Questions about this Privacy Policy or our data practices can be sent
          to{" "}
          <a
            href="mailto:hello@actshark.com"
            class="text-blue-600 hover:underline"
          >
            hello@actshark.com
          </a>
          .
        </p>
      </div>
    </main>
  );
}
